import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import 'setup/i18n.ts';
import UICard from 'components/ui/UICard';
import { theme } from 'theme';
import AppRouter from 'AppRouter';
import './App.css';
import { useAppDispatch } from 'app/hooks';
import { useContext, useEffect } from 'react';
import { commonActions } from 'app/store/common.slice';
import { adminActions } from 'features/feedback/store/admin.slice';
import { AuthContext } from 'AuthProvider';
import { localStorageItems } from 'setup/localStorage';
import FeedbackTrigger from 'features/feedback/components/FeedbackTrigger';
import FeedbackDrawer from 'features/feedback/components/FeedbackDrawer';
import { installConsoleBuffer } from 'features/feedback/collectContext/consoleBuffer';

const normalizeThemeStorage = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const availableModes = ['light', 'dark'];
  const storedMode = localStorage.getItem(localStorageItems.appThemeMode);

  if (!storedMode || !availableModes.includes(storedMode)) {
    localStorage.setItem(localStorageItems.appThemeMode, 'light');
  }

  localStorage.removeItem(localStorageItems.appThemeColorScheme);
  localStorage.removeItem(`${localStorageItems.appThemeColorScheme}-light`);
  localStorage.removeItem(`${localStorageItems.appThemeColorScheme}-dark`);
};

normalizeThemeStorage();

function App() {
  const { isAuthed } = useContext(AuthContext);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (isAuthed) {
      dispatch(commonActions.getCommonData());
      dispatch(adminActions.checkAccess());
    }
  }, [isAuthed, dispatch]);

  useEffect(() => {
    installConsoleBuffer();
  }, []);

  return (
    <ThemeProvider
      theme={theme}
      defaultMode="light"
      modeStorageKey={localStorageItems.appThemeMode}
      colorSchemeStorageKey={localStorageItems.appThemeColorScheme}
      storageWindow={null}
      disableTransitionOnChange
      noSsr
    >
      <>
        <CssBaseline />
        <UICard
          sx={{
            backgroundColor: 'primary.body',
            p: 0,
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'space-between',
            flexDirection: 'column',
            // MUI Card's default overflow:hidden establishes a scroll
            // container that breaks `position: sticky` for every
            // descendant (the rail on /wordset, the dropdown panels on
            // the header). Forcing overflow:visible at the page root
            // restores normal sticky behaviour relative to the document.
            overflow: 'visible',
          }}
        >
          <AppRouter />
          {isAuthed && (
            <>
              <FeedbackTrigger />
              <FeedbackDrawer />
            </>
          )}
        </UICard>
      </>
    </ThemeProvider>
  );
}

export default App;
